/**
 * Role switching lives in exactly ONE place: the profile (avatar)
 * dropdown, present identically on every portal.
 *
 * Historically "Switch role" buttons sat on the dashboards themselves:
 * they cleared the session role and dropped the user on the full
 * role-selection screen — a jarring detour — and in the inspected live
 * flow one was even wired into the referrer onboarding wizard. The
 * dropdown switcher instead lists every role the user holds, marks the
 * current one, and badges a pending referrer application.
 *
 * Switching ALWAYS ends in a full page reload to the role's dashboard
 * (user directive, 2026-10-12): the pick is validated server-side
 * (/api/auth/validate-role, the same gate as the login selection
 * screen) and persisted exactly like a selection-screen pick (session
 * role + remembered role), then the browser navigates to `/dashboard`
 * and the app boots fresh from server truth. In-place state-only
 * switching left stale dashboards rendered and silently no-opped
 * (G17/G18/G22); it no longer exists. A pending org role lands — after
 * the reload — on its Pending Approval gate, because the gate renders
 * from the persisted role plus the org's approval state. The
 * login-time mandatory-choice rule for non-enhanced referrers is a
 * separate login effect and is not bypassed — the switcher is only
 * reachable in-app.
 */

export interface SwitchableRoleOption {
  id: string;
  title?: string;
}

/**
 * The control bundle App hands to every dashboard: the switcher (which
 * lives only in the profile dropdown) renders `options`, marks
 * `currentRoleId`, and calls `onSelectRole` to switch (with a reload —
 * see the header). While role data is still resolving, `loading` makes
 * the section render an explicit "Loading your roles…" row instead of
 * nothing: role surfaces never render blank or vanish (G16/G17), and
 * once loaded the section never unmounts back to blank.
 */
export interface RoleSwitchControl {
  options: SwitchableRoleOption[];
  currentRoleId: string;
  onSelectRole: (roleId: string) => void;
  loading?: boolean;
}

/** Every role's dashboard lives at this one route after a switch. */
export const ROLE_DASHBOARD_PATH = '/dashboard';

import { workspaceHeader } from './workspaces';

/** Anything that can hand back a Firebase ID token (App's auth.currentUser). */
export type IdTokenSource = (forceRefresh?: boolean) => Promise<string | null | undefined>;

/**
 * Headers for role-validation / role-selection calls. Two follow-up
 * Item 1 fixes live here so BOTH call sites (the dropdown switcher and
 * the login selection screen) share them:
 *  - the token is FORCE-REFRESHED: a stale cached token can predate the
 *    org claims finalizeOrgOnboard stamped at onboard time;
 *  - the workspace header is attached (AGENTS.md rule): without it the
 *    server resolves the DEFAULT workspace context for the request.
 */
export async function roleAuthHeaders(getIdToken: IdTokenSource): Promise<Record<string, string>> {
  let token: string | null | undefined;
  try {
    token = await getIdToken(true);
  } catch {
    token = await getIdToken().catch(() => undefined);
  }
  return {
    'Content-Type': 'application/json',
    ...workspaceHeader(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export interface RoleValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * POST /api/auth/validate-role with the shared headers above. Never
 * throws: network/parse failures come back as { valid: false } with a
 * retryable message, so callers keep the user where they are.
 */
export async function requestRoleValidation(
  roleId: string,
  getIdToken: IdTokenSource,
  fetchImpl: typeof fetch = fetch,
): Promise<RoleValidationResult> {
  try {
    const headers = await roleAuthHeaders(getIdToken);
    const res = await fetchImpl('/api/auth/validate-role', {
      method: 'POST',
      headers,
      body: JSON.stringify({ role: roleId }),
    });
    const data = (await res.json().catch(() => ({}))) as { valid?: unknown; error?: string };
    if (!res.ok || data.valid !== true) {
      return { valid: false, error: data.error || 'That role is not available for your account.' };
    }
    return { valid: true };
  } catch {
    return { valid: false, error: 'Network error. Please try again.' };
  }
}

/** localStorage key for the cross-session remembered role (legacy, unscoped). */
export const REMEMBERED_ROLE_KEY = 'isats_remembered_role';

/**
 * sessionStorage key holding the signed-in user's uid, maintained by App on
 * auth state changes. persistSelectedRole reads it so the remembered role
 * can be scoped per account (Master Plan §1.2): on a shared browser, one
 * account's remembered role must never auto-apply to the next account that
 * signs in. Absent (signed out, tests, pre-auth) → the legacy unscoped key
 * is used, exactly as before.
 */
export const AUTH_UID_SESSION_KEY = 'isats_auth_uid';

/** The localStorage key the remembered role for `uid` lives under. */
export function rememberedRoleKey(uid?: string | null): string {
  return uid ? `${REMEMBERED_ROLE_KEY}:${uid}` : REMEMBERED_ROLE_KEY;
}

function currentAuthUid(): string | null {
  try {
    return sessionStorage.getItem(AUTH_UID_SESSION_KEY);
  } catch {
    return null;
  }
}

/** Persist the chosen role exactly where the boot path reads it. */
export function persistSelectedRole(roleId: string): void {
  try { sessionStorage.setItem('isats_selected_role', roleId); } catch { /* storage unavailable */ }
  try { localStorage.setItem(rememberedRoleKey(currentAuthUid()), roleId); } catch { /* storage unavailable */ }
}

/**
 * The one and only role-switch ending: persist the role, then HARD
 * reload to the dashboard so the app boots fresh from server truth.
 * `assign` is injectable for tests.
 */
export function switchRoleWithReload(
  roleId: string,
  assign: (url: string) => void = (url) => window.location.assign(url),
): void {
  persistSelectedRole(roleId);
  assign(ROLE_DASHBOARD_PATH);
}

/** One row of the avatar-dropdown role switcher. */
export interface RoleSwitchItem {
  id: string;
  title: string;
  /** The role the user is currently viewing — marked, not clickable. */
  isCurrent: boolean;
  /** Referrer application still awaiting an admin decision. */
  pendingBadge: boolean;
}

/**
 * Build the switcher rows for the profile dropdown — the ONE home of
 * role switching on every portal. The current role is marked; the
 * referrer row carries a "Pending" badge while its application awaits a
 * decision (from the referrer doc status — server truth, resolved with
 * the same mapping the dashboard card uses).
 */
export function buildRoleSwitchItems(
  options: SwitchableRoleOption[] | null | undefined,
  currentRoleId: string | null | undefined,
  referrerStatus: string | null | undefined,
): RoleSwitchItem[] {
  if (!options) return [];
  return options.map((o) => ({
    id: o.id,
    title: o.title || o.id,
    isCurrent: !!currentRoleId && o.id === currentRoleId,
    pendingBadge:
      o.id === 'referrer' &&
      !!referrerStatus &&
      !['active', 'approved', 'rejected', 'suspended', 'inactive', 'deactivated', 'disabled'].includes(
        String(referrerStatus).toLowerCase(),
      ),
  }));
}

